import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
} from "@mui/material";
import {
  ExpandMore,
  ReceiptLongOutlined,
  LocalShippingOutlined,
} from "@mui/icons-material";
import { privateApi } from "../../api/axiosConfig";


export default function PaymentList() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPayments = async () => {
      try {
        setLoading(true);
        const response = await privateApi.get("/api/v1/payments/list/");
        const data = response.data.results ? response.data.results : response.data;
        setPayments(data);
      } catch (err) {
        setError("مشکلی در دریافت اطلاعات پرداخت‌ها به وجود آمد.");
      } finally {
        setLoading(false);
      }
    };

    fetchPayments();
  }, []);

  // Helper dictionary to map backend statuses to Persian text and MUI colors
  const statusConfig = {
    pending: { label: "در انتظار", color: "warning" },
    completed: { label: "موفق / تکمیل شده", color: "success" },
    success: { label: "موفق", color: "success" },
    failed: { label: "ناموفق", color: "error" },
    cancelled: { label: "لغو شده", color: "error" },
  };

  const getStatus = (statusStr) => {
    const safeStatus = statusStr ? statusStr.toLowerCase() : "";
    return statusConfig[safeStatus] || { label: statusStr, color: "default" };
  };

  // Helper to format the Date string
  const formatDate = (dateString) => {
    const options = { 
      year: "numeric", 
      month: "long", 
      day: "numeric", 
      hour: "2-digit", 
      minute: "2-digit",
    };
    return new Date(dateString).toLocaleDateString("fa-IR", options);
  };

  // Zibal Rial conversion happens backend-side at payment creation.
  const formatToman = (toman) =>
    Math.floor(Number(toman || 0)).toLocaleString("en-US");

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh" dir="rtl">
        <CircularProgress sx={{ color: "#ef4056" }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, direction: "rtl" }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 8, direction: "rtl" }}>
      <Typography variant="h5" fontWeight={800} mb={4} color="text.primary">
        تاریخچه پرداخت‌ها
      </Typography>

      {payments.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          هیچ پرداختی تاکنون ثبت نشده است.
        </Alert>
      ) : (
        <Stack spacing={3}>
          {payments.map((payment, index) => {
            const paymentStatus = getStatus(payment.status);
            const orderStatus = getStatus(payment.order?.status);

            return (
              <Card
                key={index}
                elevation={0}
                sx={{
                  border: "1px solid",
                  borderColor: "grey.200",
                  borderRadius: 3,
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Grid container spacing={2} alignItems="center">
                    {/* Payment Info */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <ReceiptLongOutlined color="action" />
                        <Typography variant="subtitle2" color="text.secondary">
                          کد پیگیری پرداخت:
                        </Typography>
                        <Typography variant="body2" fontWeight={700}>
                          {payment.track_id || "ندارد"}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="subtitle2" color="text.secondary">
                          مبلغ پرداختی:
                        </Typography>
                        <Typography variant="h6" fontWeight={800} color="text.primary">
                          {formatToman(payment.amount)} تومان
                        </Typography>
                      </Box>
                      {Number(payment.order?.discount_amount || 0) > 0 && (
                        <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                          <Typography variant="caption" color="text.secondary">
                            شامل تخفیف:
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            color="success.main"
                          >
                            {formatToman(payment.order.discount_amount)} تومان
                          </Typography>
                        </Box>
                      )}
                    </Grid>

                    {/* Status Chips */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Box display="flex" justifyContent={{ xs: "flex-start", md: "flex-end" }} gap={1}>
                        <Chip
                          label={`پرداخت: ${paymentStatus.label}`}
                          color={paymentStatus.color}
                          size="small"
                          sx={{ fontWeight: 600, borderRadius: 1.5 }}
                        />
                        <Chip
                          label={`سفارش: ${orderStatus.label}`}
                          color={orderStatus.color}
                          variant="outlined"
                          size="small"
                          sx={{ fontWeight: 600, borderRadius: 1.5 }}
                        />
                      </Box>
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  {/* Order Details summary */}
                  {payment.order && (
                    <Box mb={2}>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            تاریخ ثبت سفارش:
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {formatDate(payment.order.created_at)}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            شماره سفارش:
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            # {payment.order.id}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                          <Box display="flex" gap={1} alignItems="flex-start">
                            <LocalShippingOutlined fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              {payment.order.shipping_address}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  )}

                  {/* Order Items Accordion */}
                  {payment.order?.order_items?.length > 0 && (
                    <Accordion
                      elevation={0}
                      disableGutters
                      sx={{
                        bgcolor: "grey.50",
                        "&:before": { display: "none" },
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "grey.200",
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="body2" fontWeight={700}>
                          مشاهده اقلام سفارش ({payment.order.order_items.length} کالا)
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Stack spacing={2} divider={<Divider />}>
                          {payment.order.order_items.map((item) => (
                            <Box
                              key={item.id}
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              {/* Left side: Image + Title + Quantity */}
                              <Box display="flex" gap={2} alignItems="center">
                                {item.product?.product_main_image && (
                                  <Box
                                    component="img"
                                    src={item.product.product_main_image}
                                    alt={item.product.product_title || "محصول"}
                                    sx={{ width: 50, height: 50, borderRadius: 2, objectFit: "cover" }}
                                  />
                                )}
                                <Box>
                                  <Typography variant="body2" fontWeight={600}>
                                    {/* Handle both Object and fallback String formats */}
                                    {typeof item.product === 'object' 
                                      ? item.product.product_title 
                                      : item.product}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    تعداد: {item.quantity}
                                  </Typography>
                                </Box>
                              </Box>

                              {/* Right side: Price (discounted snapshot at checkout) */}
                              <Typography variant="body2" fontWeight={700}>
                                {formatToman(item.price_at_purchase)} تومان
                              </Typography>
                            </Box>
                          ))}
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}
    </Container>
  );
}